import type { ReactNode } from 'react'
import { WEHUNT_APP_STORE_URL } from '../../lib/appStore'
import { COLORS } from '../../lib/theme'
import { MENU_CARD, MENU_DIVIDER, isInAppHref } from './topbarChrome'

export interface MenuColumn {
  title: string
  items: { label: string; href: string }[]
}

/**
 * The pale card behind both header surfaces on wehunt.app. The menu is this
 * card holding the link columns; search is the same card with a field stacked
 * above them — which is why it lives here rather than inside either one.
 */
export function MenuCard({ mobile, children }: { mobile: boolean; children: ReactNode }) {
  return (
    <div
      style={{
        width: '100%',
        padding: mobile ? MENU_CARD.paddingMobile : MENU_CARD.padding,
        background: COLORS.secondaryContainer,
        color: COLORS.onSecondaryContainer,
        borderRadius: MENU_CARD.radius,
        boxShadow: '0 18px 48px rgba(20, 40, 20, 0.22)',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  )
}

/**
 * The menu's link columns, spanning the card. Columns after the first carry a
 * hairline and a matching inset, so the rule sits midway between two columns —
 * the gap supplies one half and the padding the other. The stacked phone
 * layout lives in styles/components.css (`.wh-menu-columns`).
 *
 * There is no decorative panel: wehunt.app ships markup for one but computes it
 * to `display: none`, so its menu is columns edge to edge. Matching the site
 * means not drawing one.
 */
export function MenuColumns({ columns, onNavigate, onNavigateTo }: {
  columns: MenuColumn[]
  /** Called after any link is picked — used to dismiss the surface. */
  onNavigate?: () => void
  /**
   * Supply this to route in-app: for a link into this app the anchor's default
   * is prevented and the href handed over instead, so a shell whose menu points
   * at its own routes does not take a full page load. Absolute URLs (the shop)
   * are always left to the browser. The href stays on the element either way,
   * so middle-click and "copy link" keep working.
   */
  onNavigateTo?: (href: string) => void
}) {
  return (
    <div className="wh-menu-inner">
      <div className="wh-menu-columns">
        {columns.map((col, i) => (
          <div
            key={col.title}
            className="wh-menu-column"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: MENU_CARD.columnStackGap,
              ...(i > 0
                ? { paddingLeft: MENU_CARD.columnGap, borderLeft: `1px solid ${MENU_DIVIDER}` }
                : null),
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: MENU_CARD.titleSize,
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'inherit',
              }}
            >
              {col.title}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: MENU_CARD.linkGap }}>
              {col.items.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => {
                    if (onNavigateTo && isInAppHref(item.href)) {
                      e.preventDefault()
                      onNavigateTo(item.href)
                    }
                    onNavigate?.()
                  }}
                  style={{
                    fontSize: MENU_CARD.linkSize,
                    fontWeight: 400,
                    lineHeight: 1.3,
                    color: 'inherit',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.65'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1'
                  }}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StoreBadge({ kind }: { kind: 'app-store' | 'google-play' }) {
  const isApple = kind === 'app-store'
  return (
    <a
      href={WEHUNT_APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 40,
        padding: '0 14px',
        background: '#000',
        color: '#fff',
        borderRadius: 6,
        textDecoration: 'none',
      }}
    >
      {isApple ? (
        <svg width="20" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden>
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ) : (
        <svg width="20" height="22" viewBox="0 0 24 24" aria-hidden>
          <path d="M3.6 1.7c-.3.3-.5.7-.5 1.3v18c0 .6.2 1 .5 1.3L13.7 12 3.6 1.7z" fill="#34a853" />
          <path d="M17.4 8.7l-3.7 2.1L4.4 1l13 7.7z" fill="#fbbc04" />
          <path d="M17.4 15.3L4.4 23l9.3-9.8 3.7 2.1z" fill="#ea4335" />
          <path d="M17.4 8.7l3.5 2c.9.5.9 1.6 0 2.1l-3.5 2-3.7-3.1 3.7-3z" fill="#4285f4" />
        </svg>
      )}
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.05 }}>
        <span style={{ fontSize: 9, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {isApple ? 'Download on the' : 'Get it on'}
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.2 }}>
          {isApple ? 'App Store' : 'Google Play'}
        </span>
      </span>
    </a>
  )
}

/** "Download the app" plus the two store badges, under a hairline. */
export function MenuFooter({ title = 'Download the app' }: { title?: string }) {
  return (
    <div
      style={{
        marginTop: 24,
        paddingTop: 24,
        borderTop: `1px solid ${MENU_DIVIDER}`,
      }}
    >
      <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>{title}</p>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <StoreBadge kind="app-store" />
        <StoreBadge kind="google-play" />
      </div>
    </div>
  )
}
